import React, { useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  makeStyles,
  Tooltip,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@material-ui/core";
import {
  Layers,
  DeleteOutline,
  Edit,
  Add,
  ExpandMore,
  Info,
  Label,
  Code,
  PriorityHigh,
} from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  layerItem: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.mode === 'dark' ? '#374151' : "#f5f5f5",
    borderRadius: "8px",
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : "#eeeeee",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  
  layerHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  
  layerInfo: {
    flex: 1,
    minWidth: 0,
  },
  
  layerName: {
    fontWeight: "bold",
    fontSize: "14px",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  
  layerMeta: {
    fontSize: "12px",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  
  layerActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  
  layerTypeChip: {
    height: "24px",
    fontSize: "11px",
  },
  
  priorityChip: {
    backgroundColor: "#ff9800",
    color: "white",
    height: "20px",
    fontSize: "10px",
  },
  
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
    height: "20px",
    fontSize: "10px",
  },
  
  inactiveChip: {
    backgroundColor: "#9e9e9e",
    color: "white",
    height: "20px",
    fontSize: "10px",
  },
  
  deleteButton: {
    color: "#d32f2f",
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.1)",
    },
  },
  
  editButton: {
    color: theme.palette.primary.main,
    padding: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.1)",
    },
  },
  
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  
  layerDialog: {
    "& .MuiDialog-paper": {
      minWidth: "600px",
      maxWidth: "800px",
    },
  },
  
  dialogContent: {
    padding: theme.spacing(2),
  },
  
  formField: {
    marginBottom: theme.spacing(2),
  },
  
  keywordsInput: {
    marginTop: theme.spacing(1),
  },
  
  layerContentField: {
    "& .MuiOutlinedInput-root": {
      minHeight: "150px",
      alignItems: "flex-start",
    },
    "& textarea": {
      minHeight: "150px",
      overflow: "auto !important",
    },
  },
  
  accordion: {
    marginTop: theme.spacing(1),
    "&:before": {
      display: "none",
    },
  },
  
  accordionSummary: {
    backgroundColor: theme.mode === 'dark' ? '#2d3748' : "#f0f0f0",
    borderRadius: "4px",
    minHeight: "48px",
    "&.Mui-expanded": {
      minHeight: "48px",
    },
  },
  
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column",
  },
}));

const PromptLayersManager = ({ promptId, companyId, onLayersChange }) => {
  const classes = useStyles();
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLayer, setEditingLayer] = useState(null);
  const [formData, setFormData] = useState({
    layerName: "",
    layerType: "contextual",
    triggerKeywords: [],
    triggerIntent: "",
    layerContent: "",
    priority: 0,
    isActive: true,
    maxTokens: null,
    temperature: null,
  });
  const [keywordsInput, setKeywordsInput] = useState("");
  
  // ✅ Usar useRef para rastrear si el componente está montado
  const isMountedRef = useRef(true);

  // ✅ Función fetchLayers reutilizable que respeta el estado del componente
  const fetchLayers = React.useCallback(async () => {
    if (!promptId) {
      if (isMountedRef.current) {
        setLayers([]);
      }
      return;
    }
    
    if (isMountedRef.current) {
      setLoading(true);
    }
    
    try {
      const { data } = await api.get(`/prompt/${promptId}/layers`);
      if (isMountedRef.current) {
        setLayers(data || []);
        if (onLayersChange) {
          onLayersChange(data || []);
        }
      }
    } catch (error) {
      console.error("Error cargando layers:", error);
      if (isMountedRef.current) {
        toastError(error);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [promptId, onLayersChange]);

  // Cargar layers al montar o cuando cambie promptId
  React.useEffect(() => {
    isMountedRef.current = true;
    fetchLayers();

    return () => {
      isMountedRef.current = false;
    };
  }, [promptId, fetchLayers]);

  const handleOpenCreateDialog = () => {
    setEditingLayer(null);
    setFormData({
      layerName: "",
      layerType: "contextual",
      triggerKeywords: [],
      triggerIntent: "",
      layerContent: "",
      priority: 0,
      isActive: true,
      maxTokens: null,
      temperature: null,
    });
    setKeywordsInput("");
    setEditDialogOpen(true);
  };

  const handleOpenEditDialog = (layer) => {
    setEditingLayer(layer);
    setFormData({
      layerName: layer.layerName || "",
      layerType: layer.layerType || "contextual",
      triggerKeywords: Array.isArray(layer.triggerKeywords) ? layer.triggerKeywords : [],
      triggerIntent: layer.triggerIntent || "",
      layerContent: layer.layerContent || "",
      priority: layer.priority || 0,
      isActive: layer.isActive !== undefined ? layer.isActive : true,
      maxTokens: layer.maxTokens || null,
      temperature: layer.temperature || null,
    });
    setKeywordsInput(Array.isArray(layer.triggerKeywords) ? layer.triggerKeywords.join(", ") : "");
    setEditDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEditDialogOpen(false);
    setEditingLayer(null);
    setFormData({
      layerName: "",
      layerType: "contextual",
      triggerKeywords: [],
      triggerIntent: "",
      layerContent: "",
      priority: 0,
      isActive: true,
      maxTokens: null,
      temperature: null,
    });
    setKeywordsInput("");
  };

  const handleKeywordsChange = (value) => {
    setKeywordsInput(value);
    // Convertir string separado por comas a array
    const keywords = value
      .split(",")
      .map(k => k.trim())
      .filter(k => k.length > 0);
    setFormData({ ...formData, triggerKeywords: keywords });
  };

  const handleSaveLayer = async () => {
    if (!formData.layerName || !formData.layerContent) {
      toast.error("Nombre y contenido de la capa son obligatorios");
      return;
    }

    try {
      if (editingLayer) {
        // Actualizar
        await api.put(`/prompt/${promptId}/layers/${editingLayer.id}`, formData);
        toast.success("Capa actualizada correctamente");
      } else {
        // Crear
        await api.post(`/prompt/${promptId}/layers`, formData);
        toast.success("Capa creada correctamente");
      }
      handleCloseDialog();
      fetchLayers();
    } catch (error) {
      console.error("Error guardando layer:", error);
      toastError(error);
    }
  };

  const handleDeleteLayer = async (layerId) => {
    if (!window.confirm("¿Estás seguro de eliminar esta capa?")) {
      return;
    }

    try {
      await api.delete(`/prompt/${promptId}/layers/${layerId}`);
      toast.success("Capa eliminada correctamente");
      fetchLayers();
    } catch (error) {
      console.error("Error eliminando layer:", error);
      toastError(error);
    }
  };

  const getLayerTypeLabel = (type) => {
    const labels = {
      contextual: "Contextual",
      knowledge: "Conocimiento",
      fallback: "Fallback",
    };
    return labels[type] || type;
  };

  const getLayerTypeColor = (type) => {
    const colors = {
      contextual: "#1976d2",
      knowledge: "#9c27b0",
      fallback: "#f57c00",
    };
    return colors[type] || "#757575";
  };

  if (!promptId) {
    return (
      <Paper className={classes.emptyState}>
        <Typography variant="body2" color="textSecondary">
          Guarda el prompt primero para gestionar capas contextuales
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
        <Typography variant="h6" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Layers style={{ marginRight: 8 }} />
          Capas Contextuales
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Add />}
          onClick={handleOpenCreateDialog}
          disabled={loading}
        >
          Agregar Capa
        </Button>
      </Box>

      {loading && layers.length === 0 ? (
        <Typography variant="body2" color="textSecondary" style={{ textAlign: "center", padding: 16 }}>
          Cargando capas...
        </Typography>
      ) : layers.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Layers style={{ fontSize: 48, color: "#ccc", marginBottom: 16 }} />
          <Typography variant="body2" color="textSecondary">
            No hay capas configuradas
          </Typography>
          <Typography variant="caption" color="textSecondary" style={{ marginTop: 8 }}>
            Las capas contextuales se activan automáticamente según la intención del usuario
          </Typography>
        </Paper>
      ) : (
        layers.map((layer) => (
          <Paper key={layer.id} className={classes.layerItem}>
            <Box className={classes.layerHeader}>
              <Box className={classes.layerInfo}>
                <Typography component="div" className={classes.layerName}>
                  {layer.layerName}
                  <Chip
                    label={getLayerTypeLabel(layer.layerType)}
                    size="small"
                    className={classes.layerTypeChip}
                    style={{ backgroundColor: getLayerTypeColor(layer.layerType), color: "white" }}
                  />
                  {layer.isActive ? (
                    <Chip label="Activa" size="small" className={classes.activeChip} />
                  ) : (
                    <Chip label="Inactiva" size="small" className={classes.inactiveChip} />
                  )}
                  {layer.priority > 0 && (
                    <Chip
                      label={`Prioridad: ${layer.priority}`}
                      size="small"
                      className={classes.priorityChip}
                    />
                  )}
                </Typography>
                <Box className={classes.layerMeta}>
                  {layer.triggerIntent && (
                    <Chip
                      icon={<Label />}
                      label={`Intención: ${layer.triggerIntent}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {Array.isArray(layer.triggerKeywords) && layer.triggerKeywords.length > 0 && (
                    <Chip
                      icon={<Code />}
                      label={`${layer.triggerKeywords.length} keyword(s)`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
              <Box className={classes.layerActions}>
                <Tooltip title="Editar capa">
                  <IconButton
                    size="small"
                    className={classes.editButton}
                    onClick={() => handleOpenEditDialog(layer)}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar capa">
                  <IconButton
                    size="small"
                    className={classes.deleteButton}
                    onClick={() => handleDeleteLayer(layer.id)}
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Accordion className={classes.accordion}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                className={classes.accordionSummary}
              >
                <Typography variant="caption" color="textSecondary">
                  Ver contenido de la capa
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                  {layer.layerContent}
                </Typography>
                {Array.isArray(layer.triggerKeywords) && layer.triggerKeywords.length > 0 && (
                  <Box marginTop={2}>
                    <Typography variant="caption" color="textSecondary">
                      Keywords: {layer.triggerKeywords.join(", ")}
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Paper>
        ))
      )}

      {/* Dialog para crear/editar capa */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseDialog}
        className={classes.layerDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingLayer ? "Editar Capa Contextual" : "Nueva Capa Contextual"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            fullWidth
            label="Nombre de la Capa"
            value={formData.layerName}
            onChange={(e) => setFormData({ ...formData, layerName: e.target.value })}
            className={classes.formField}
            required
            helperText="Nombre descriptivo para identificar esta capa"
          />

          <FormControl fullWidth className={classes.formField}>
            <InputLabel>Tipo de Capa</InputLabel>
            <Select
              value={formData.layerType}
              onChange={(e) => setFormData({ ...formData, layerType: e.target.value })}
              label="Tipo de Capa"
            >
              <MenuItem value="contextual">Contextual (Se activa según intención)</MenuItem>
              <MenuItem value="knowledge">Conocimiento (Base de conocimiento)</MenuItem>
              <MenuItem value="fallback">Fallback (Capa por defecto)</MenuItem>
            </Select>
            <FormHelperText>
              Las capas contextuales se activan cuando se detecta la intención del usuario
            </FormHelperText>
          </FormControl>

          <TextField
            fullWidth
            label="Intención (Opcional)"
            value={formData.triggerIntent}
            onChange={(e) => setFormData({ ...formData, triggerIntent: e.target.value })}
            className={classes.formField}
            helperText="Intención que activa esta capa (ej: precio, compra, garantia, informacion, soporte)"
            placeholder="precio"
          />

          <TextField
            fullWidth
            label="Palabras Clave (Opcional)"
            value={keywordsInput}
            onChange={(e) => handleKeywordsChange(e.target.value)}
            className={`${classes.formField} ${classes.keywordsInput}`}
            helperText="Palabras clave separadas por comas que activan esta capa (ej: comprar, precio, costo)"
            placeholder="comprar, precio, costo"
          />

          <TextField
            fullWidth
            label="Contenido de la Capa"
            value={formData.layerContent}
            onChange={(e) => setFormData({ ...formData, layerContent: e.target.value })}
            className={`${classes.formField} ${classes.layerContentField}`}
            multiline
            rows={6}
            required
            helperText="Contenido adicional que se agregará al prompt cuando esta capa se active"
            placeholder="Ejemplo: Cuando el usuario pregunta por precio, menciona que tenemos ofertas especiales..."
          />
          
          <Box display="flex" justifyContent="flex-end" marginTop={0.5}>
            <Typography variant="caption" color="textSecondary">
              Cantidad de caracteres: {(formData.layerContent || "").length}
            </Typography>
          </Box>

          <Box display="flex" gap={2} className={classes.formField}>
            <TextField
              label="Prioridad"
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0, max: 100 }}
              helperText="Mayor prioridad = se activa primero"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  color="primary"
                />
              }
              label="Activa"
            />
          </Box>

          <Box display="flex" gap={2} className={classes.formField}>
            <TextField
              label="Max Tokens (Opcional)"
              type="number"
              value={formData.maxTokens || ""}
              onChange={(e) => setFormData({ ...formData, maxTokens: e.target.value ? parseInt(e.target.value) : null })}
              inputProps={{ min: 1 }}
              helperText="Tokens máximos para esta capa"
            />
            <TextField
              label="Temperatura (Opcional)"
              type="number"
              value={formData.temperature !== null && formData.temperature !== undefined ? Number(formData.temperature) : ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || value === null) {
                  setFormData({ ...formData, temperature: null });
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue)) {
                    // Validar rango
                    if (numValue >= 0 && numValue <= 2) {
                      // Guardar el valor numérico directamente (sin formateo adicional)
                      setFormData({ ...formData, temperature: numValue });
                    }
                  }
                }
              }}
              onBlur={(e) => {
                // Al perder el foco, normalizar el valor (eliminar ceros innecesarios)
                const value = e.target.value;
                if (value && value !== "" && !isNaN(parseFloat(value))) {
                  const numValue = parseFloat(value);
                  if (numValue >= 0 && numValue <= 2) {
                    // Convertir a número y luego de vuelta a string para eliminar ceros innecesarios
                    // Ejemplo: 0.60 -> 0.6, 1.00 -> 1
                    const normalized = parseFloat(numValue.toString());
                    setFormData({ ...formData, temperature: normalized });
                  }
                }
              }}
              inputProps={{ min: 0, max: 2, step: 0.1 }}
              helperText="Temperatura específica para esta capa (0.0 - 2.0)"
            />
          </Box>

          <Box marginTop={2} padding={2} style={{ backgroundColor: "#e3f2fd", borderRadius: 4 }}>
            <Typography variant="caption" color="textSecondary" style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Info fontSize="small" />
              <strong>Tip:</strong> Las capas se activan automáticamente cuando el mensaje del usuario contiene las palabras clave o coincide con la intención configurada.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSaveLayer} color="primary" variant="contained">
            {editingLayer ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptLayersManager;
